import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Languages, Check } from 'lucide-react';
import clsx from 'clsx';
import { SUPPORTED_LANGUAGES, LANGUAGE_LABELS, type Language } from '../i18n';

/**
 * Language switcher for the eyebrow bar.
 *
 * Picking a language writes it to localStorage, which also stops the browser
 * detector from overriding the choice on the next visit — otherwise a German
 * browser would silently switch back and the control would look broken.
 */
export default function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onPointer = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // resolvedLanguage rather than language: with "de-AT" the former gives "de",
  // which is what we have resources for.
  const current = (i18n.resolvedLanguage ?? 'en') as Language;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('common.language')}
        title={t('common.language')}
        className={clsx(
          'flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium transition-colors',
          open
            ? 'bg-slate-200 text-slate-900'
            : 'text-slate-600 hover:bg-slate-200 hover:text-slate-900',
        )}
      >
        <Languages size={14} />
        <span className="uppercase">{current}</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-1 w-44 overflow-hidden rounded-lg border border-slate-200 bg-surface shadow-lg"
        >
          <div className="border-b border-slate-100 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
            {t('common.language')}
          </div>
          {SUPPORTED_LANGUAGES.map((lng) => (
            <button
              key={lng}
              role="menuitem"
              onClick={() => {
                void i18n.changeLanguage(lng);
                setOpen(false);
              }}
              className="flex w-full items-center justify-between px-3 py-2.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-50"
            >
              <span>
                {LANGUAGE_LABELS[lng].native}
                {/* The English name too, so someone who cannot read the current
                    language can still find their own. */}
                {LANGUAGE_LABELS[lng].native !== LANGUAGE_LABELS[lng].label && (
                  <span className="ml-1.5 text-xs text-slate-400">
                    {LANGUAGE_LABELS[lng].label}
                  </span>
                )}
              </span>
              {current === lng && <Check size={14} className="shrink-0 text-brand-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
